import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { authService } from '../services/auth';
import { Lock, key, Eye, EyeOff, Shield, CheckCircle, ArrowRight } from 'lucide-react';

const ResetPasswordPage = () => {
  const { uid, token } = useParams();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    new_password: '',
    confirm_password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.new_password !== formData.confirm_password) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    if (formData.new_password.length < 8) {
      toast.error('La contraseña debe tener al menos 8 caracteres');
      return;
    }

    setLoading(true);

    try {
      await authService.confirmPasswordReset({
        uid,
        token,
        new_password: formData.new_password
      });
      
      setSubmitted(true);
      toast.success('Contraseña actualizada exitosamente');
      
      // Esperar un momento para que el usuario lea el mensaje
      setTimeout(() => {
        navigate('/app/login');
      }, 3000);
      
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al restablecer contraseña. El enlace puede haber expirado.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex bg-gray-50">
        <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-20 xl:px-24 bg-white">
          <div className="mx-auto w-full max-w-sm text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              ¡Contraseña Actualizada!
            </h2>
            
            <p className="text-gray-600 mb-8 leading-relaxed">
              Tu contraseña ha sido modificada correctamente. Ahora puedes iniciar sesión con tus nuevas credenciales.
            </p>

            <Link
              to="/app/login"
              className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-xl text-base font-semibold text-white bg-[#2A9D8F] hover:bg-[#E76F51] transition-all duration-300 shadow-lg shadow-[#2A9D8F]/30"
            >
              Iniciar Sesión Ahora
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </div>
        </div>
        
        {/* Panel Derecho Decorativo */}
        <div className="hidden lg:flex flex-1 relative bg-gradient-to-br from-[#F4E1D2] to-[#FFE8D6]">
          <div className="absolute inset-0 opacity-30">
            <div className="absolute top-20 right-20 w-64 h-64 bg-[#2A9D8F]/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-32 left-16 w-48 h-48 bg-[#E76F51]/10 rounded-full blur-3xl"></div>
          </div>
          <div className="relative flex flex-col justify-center px-16 xl:px-20 text-center">
             <Shield className="w-24 h-24 text-[#2A9D8F] mx-auto mb-6 opacity-80" />
             <h3 className="text-2xl font-bold text-gray-800 mb-2">Cuenta Protegida</h3>
             <p className="text-gray-600">Tu seguridad es nuestra prioridad.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* ============================================= */}
      {/* SECCIÓN IZQUIERDA - FORMULARIO */}
      {/* ============================================= */}
      <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-20 xl:px-24 bg-white">
        <div className="mx-auto w-full max-w-md">
          {/* Logo y Header */}
          <div className="text-center mb-10">
            <img
              src="/logo-symbol.png"
              alt="EstetikFlow"
              className="h-16 w-auto mx-auto mb-4"
            />
            <h1 className="text-3xl font-bold text-[#1F7A6F] tracking-tight">
              Nueva Contraseña
            </h1>
            <p className="mt-3 text-gray-500 text-base">
              Por favor, crea una contraseña segura para tu cuenta.
            </p>
          </div>

          {/* Formulario */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Nueva Contraseña */}
            <div>
              <label htmlFor="new_password" class="block text-sm font-medium text-gray-700 mb-2">
                Nueva Contraseña
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="new_password"
                  name="new_password"
                  type={showPassword ? "text" : "password"}
                  required
                  value={formData.new_password}
                  onChange={(e) => setFormData({ ...formData, new_password: e.target.value })}
                  className="block w-full pl-12 pr-12 py-3.5 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 bg-gray-50/50 focus:outline-none focus:ring-2 focus:ring-[#2A9D8F] focus:border-[#2A9D8F] focus:bg-white transition-all duration-200"
                  placeholder="Mínimo 8 caracteres"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Confirmar Contraseña */}
            <div>
              <label htmlFor="confirm_password" class="block text-sm font-medium text-gray-700 mb-2">
                Confirmar Contraseña
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="confirm_password"
                  name="confirm_password"
                  type={showConfirmPassword ? "text" : "password"}
                  required
                  value={formData.confirm_password}
                  onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                  className="block w-full pl-12 pr-12 py-3.5 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 bg-gray-50/50 focus:outline-none focus:ring-2 focus:ring-[#2A9D8F] focus:border-[#2A9D8F] focus:bg-white transition-all duration-200"
                  placeholder="Repite tu contraseña"
                />
                 <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center items-center py-4 px-6 border border-transparent rounded-xl text-base font-semibold text-white bg-[#2A9D8F] hover:bg-[#E76F51] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2A9D8F] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg shadow-[#2A9D8F]/30 hover:shadow-xl hover:shadow-[#E76F51]/40 transform hover:-translate-y-0.5 active:translate-y-0"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                       <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Actualizando...
                  </>
                ) : (
                  'Establecer Nueva Contraseña'
                )}
              </button>
            </div>
          </form>
          
          <div className="mt-6 text-center">
             <Link
               to="/app/login"
               className="font-medium text-gray-500 hover:text-[#2a9d8f] transition-colors"
             >
               Cancelar
             </Link>
           </div>
        </div>
      </div>

      {/* ============================================= */}
      {/* SECCIÓN DERECHA - VISUAL */}
      {/* ============================================= */}
      <div className="hidden lg:flex flex-1 relative bg-gradient-to-br from-[#F4E1D2] to-[#FFE8D6]">
        {/* Patrón decorativo */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 right-20 w-64 h-64 bg-[#2A9D8F]/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-32 left-16 w-48 h-48 bg-[#E76F51]/10 rounded-full blur-3xl"></div>
        </div>

        {/* Contenido */}
        <div className="relative flex flex-col justify-center px-16 xl:px-20">
          <div className="max-w-md mx-auto text-center space-y-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white/60 backdrop-blur-md rounded-2xl shadow-sm mb-4">
              <Lock className="w-10 h-10 text-[#2A9D8F]" />
            </div>
            
            <div>
              <h3 className="text-2xl font-bold text-gray-800 mb-3">
                Tu seguridad es lo primero
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Elige una contraseña robusta que combine letras, números y símbolos para mantener tu cuenta protegida.
              </p>
            </div>

            <div className="bg-white/50 backdrop-blur-sm rounded-xl p-6 border border-white/60 text-left">
              <h4 className="font-semibold text-gray-800 text-sm mb-2">Recomendaciones:</h4>
              <ul className="space-y-2">
                 <li className="flex items-center text-xs text-gray-600">
                    <CheckCircle className="w-4 h-4 text-[#2A9D8F] mr-2" />
                    Mínimo 8 caracteres
                 </li>
                 <li className="flex items-center text-xs text-gray-600">
                    <CheckCircle className="w-4 h-4 text-[#2A9D8F] mr-2" />
                    Combina mayúsculas y minúsculas
                 </li>
                 <li className="flex items-center text-xs text-gray-600">
                    <CheckCircle className="w-4 h-4 text-[#2A9D8F] mr-2" />
                    Usa números o símbolos (!@#)
                 </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
